'use client';

import { Types } from '@scorenco/core';
import { useEffect, useMemo, useState } from 'react';

const getStorageKeys = (widgetId: number) => {
  const id = widgetId ? String(widgetId) : 'unknown';
  return {
    session: `widgets:popupAd:session:${id}`,
    lastTime: `widgets:popupAd:lastTime:${id}`,
  };
};

const hasCooldownElapsed = (lastTime: number, cooldownMs: number) => {
  return Date.now() - lastTime > cooldownMs;
};

/**
 * Gère l'éligibilité de la popup pub : cooldown, session, et ouverture après délai.
 */
export const usePopupAdEligibility = ({
  widget,
  delayMs,
  cooldownHours,
}: {
  widget: Types.Widgets_Widget;
  delayMs: number;
  cooldownHours: number;
}) => {
  const keys = useMemo(() => getStorageKeys(widget.id), [widget.id]);
  const cooldownMs = cooldownHours * 60 * 60 * 1000;

  const isEligible = useMemo(() => {
    if (typeof window === 'undefined' || window.innerWidth < 260) {
      return false;
    }

    try {
      if (document.referrer.includes('admin.scorenco.com')) {
        return false;
      }
    } catch {
      // ignore
    }

    try {
      const seenThisSession = sessionStorage.getItem(keys.session);
      const lastTimeStr = localStorage.getItem(keys.lastTime);
      const lastTime = lastTimeStr ? Number(lastTimeStr) : 0;

      return (
        !seenThisSession &&
        (lastTime === 0 || hasCooldownElapsed(lastTime, cooldownMs))
      );
    } catch {
      return false;
    }
  }, [keys.session, keys.lastTime, cooldownMs]);

  const [isOpen, setIsOpen] = useState(isEligible);

  // Ouvrir la popup après le délai si une pub est disponible
  useEffect(() => {
    if (!isEligible) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem(keys.session, '1');
      localStorage.setItem(keys.lastTime, String(Date.now()));
    }, delayMs);

    return () => clearTimeout(timer);
  }, [isEligible, keys.session, keys.lastTime, delayMs]);

  return { isEligible, isOpen, setIsOpen };
};
